import { Banner, Select } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { usd } from './agentFormat';
import { SkeletonCards } from '../../components/ui/Skeleton';

interface Day {
  day: string; turns: number; tokensIn: number; tokensOut: number; tools: number; p95Ms: number; p95FirstMs: number;
  errors: number; fallbacks: number; costMicroUsd: number; toolErrors: number;
}
interface Voice { sttSeconds: number; ttsChars: number; ttsCachedChars: number; costMicroUsd: number; costPartial: boolean }
interface Metrics {
  days: number;
  conversations: number;
  ratings: { up: number; down: number };
  usage: { available: true; days: Day[]; totals: Omit<Day, 'day'>; voice?: Voice } | { available: false; message: string };
}

const n = (v: number) => v.toLocaleString('pt-BR');
const pct = (part: number, total: number) => (total ? `${(Math.round((part / total) * 1000) / 10).toLocaleString('pt-BR')}%` : '—');
const minutes = (s: number) => (s < 60 ? `${n(s)} s` : `${(s / 60).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} min`);
const secs = (ms: number) => (ms ? `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s` : '—');

const PERIOD_OPTIONS = [
  { value: '7', label: 'últimos 7 dias' },
  { value: '30', label: 'últimos 30 dias' },
  { value: '90', label: 'últimos 90 dias' },
];

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="p-3" style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
      <div className="text-xs !text-[var(--text-muted)]">{label}</div>
      <div className="font-semibold" style={{ fontSize: 20 }}>{value}</div>
      {hint && <div className="text-xs !text-[var(--text-muted)]">{hint}</div>}
    </div>
  );
}

/** Métricas da versão publicada: turnos, tokens e latência (Analytics Engine) e 👍/👎 do widget */
export function AgentMetricsPanel({ agentId }: { agentId: string }) {
  const fetchApi = useAuthenticatedFetch();
  const [days, setDays] = useState(7);
  const [m, setM] = useState<Metrics | null>(null);

  useEffect(() => {
    let alive = true;
    fetchApi(`/api/ai/agents/${agentId}/metrics?days=${days}`).then((r) => (r.ok ? r.json() : null)).then((j) => { if (alive) setM(j); }).catch(() => undefined);
    return () => { alive = false; };
  }, [agentId, days, fetchApi]);

  const rated = m ? m.ratings.up + m.ratings.down : 0;
  const periodOption = PERIOD_OPTIONS.find((o) => o.value === String(days)) ?? null;
  return (
    <section className="space-y-3" data-testid="ai-agent-metrics">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Métricas</h3>
        <Select value={(periodOption)?.value ?? null} items={PERIOD_OPTIONS} onValueChange={(value: string | null) => {
            const v = value;
            if (v) setDays(Number(v));
          }} aria-label="Período" />
      </div>
      {!m && <SkeletonCards count={4} label="Carregando métricas" />}
      {m && (
        <>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {m.usage.available && <Tile label="Respostas" value={n(m.usage.totals.turns)} />}
            {m.usage.available && <Tile label="Tempo até a 1ª palavra (p95)" value={secs(m.usage.totals.p95FirstMs)} hint="meta: até 3 s" />}
            {m.usage.available && <Tile label="Resposta completa (p95)" value={secs(m.usage.totals.p95Ms)} />}
            {m.usage.available && <Tile label="Tokens" value={n(m.usage.totals.tokensIn + m.usage.totals.tokensOut)} hint={`${n(m.usage.totals.tokensIn)} entrada · ${n(m.usage.totals.tokensOut)} saída`} />}
            {m.usage.available && <Tile label="Ferramentas usadas" value={n(m.usage.totals.tools)} hint={m.usage.totals.toolErrors ? `${n(m.usage.totals.toolErrors)} com erro` : undefined} />}
            {m.usage.available && <Tile label="Taxa de erro" value={pct(m.usage.totals.errors, m.usage.totals.turns)} hint={`${n(m.usage.totals.errors)} respostas com falha`} />}
            {m.usage.available && <Tile label="Modelo de reserva" value={pct(m.usage.totals.fallbacks, m.usage.totals.turns)} hint={`${n(m.usage.totals.fallbacks)} respostas`} />}
            {m.usage.available && <Tile label="Custo estimado" value={usd(m.usage.totals.costMicroUsd)} hint={m.usage.totals.turns ? `${usd(Math.round(m.usage.totals.costMicroUsd / m.usage.totals.turns))} por resposta` : undefined} />}
            {m.usage.available && m.usage.voice && (m.usage.voice.sttSeconds > 0 || m.usage.voice.ttsChars > 0) && <>
              <Tile label="Voz: áudio transcrito" value={minutes(m.usage.voice.sttSeconds)} hint="microfone do visitante" />
              <Tile label="Voz: falado" value={`${n(m.usage.voice.ttsChars)} caracteres`} hint={m.usage.voice.ttsCachedChars ? `${n(m.usage.voice.ttsCachedChars)} vieram do cache` : undefined} />
              <Tile label="Custo da voz" value={usd(m.usage.voice.costMicroUsd)} hint={m.usage.voice.costPartial ? 'parcial: a fala não tem preço configurado' : 'transcrição e fala'} />
            </>}
            <Tile label="Conversas no widget" value={n(m.conversations)} />
            <Tile label="Avaliações" value={rated ? `${Math.round((m.ratings.up / rated) * 100)}% 👍` : '—'} hint={rated ? `${n(m.ratings.up)} 👍 · ${n(m.ratings.down)} 👎` : 'nenhuma ainda'} />
          </div>
          {!m.usage.available && <Banner variant="default" description={<>Uso da versão no ar indisponível: {m.usage.message}</>} />}
          {m.usage.available && m.usage.days.length > 0 && (
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                  <th className="py-1">Dia</th><th className="py-1" style={{ textAlign: 'right' }}>Respostas</th><th className="py-1" style={{ textAlign: 'right' }}>1ª palavra (p95)</th><th className="py-1" style={{ textAlign: 'right' }}>Tokens</th><th className="py-1" style={{ textAlign: 'right' }}>Erros</th><th className="py-1" style={{ textAlign: 'right' }}>Custo</th>
                </tr>
              </thead>
              <tbody>
                {m.usage.days.map((d) => (
                  <tr key={d.day} style={{ borderTop: '1px solid var(--border)' }}>
                    <td className="py-1">{new Date(`${d.day}T12:00:00`).toLocaleDateString('pt-BR')}</td>
                    <td className="py-1" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n(d.turns)}</td>
                    <td className="py-1" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{secs(d.p95FirstMs)}</td>
                    <td className="py-1" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n(d.tokensIn + d.tokensOut)}</td>
                    <td className="py-1" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n(d.errors)}</td>
                    <td className="py-1" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{usd(d.costMicroUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {m.usage.available && m.usage.days.length === 0 && <div className="text-sm !text-[var(--text-muted)]">Sem conversas com a versão no ar neste período.</div>}
        </>
      )}
    </section>
  );
}
